import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BASE_URL } from '../constants.js'
import {
  type CreateResultsStatus,
  type CreateResultStatus,
  UserMessageError,
  useAuth
} from 'prove-common'

interface CreateResultsMutationVariables {
  file: File
  fileName: string
}

export const useCreateResultsMutation = () => {
  const queryClient = useQueryClient()
  const { authenticatedFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ file, fileName }: CreateResultsMutationVariables) => {
      const formData = new FormData()
      formData.append('files', file, fileName)
      const response = await authenticatedFetch(`${BASE_URL}/results`, {
        body: formData,
        method: 'post'
      })
      if (!response.ok) {
        throw new Error('Rapporten ble ikke mottatt. Prøv igjen.')
      }
      const createResultsStatus: CreateResultsStatus = await response.json()
      const status: CreateResultStatus | undefined =
        createResultsStatus.statuses?.at(0)
      if (
        status?.filename === fileName &&
        status.status === 'RECEIVED_AWAITING_SCAN'
      ) {
        return createResultsStatus
      } else {
        if (
          status?.status === 'A resource with this identifier already exists'
        ) {
          throw new UserMessageError(
            'A report with this file name has already been uploaded. Check the existing report before uploading again.'
          )
        }
        if (
          status?.status ===
          'The resource is in an incompatible state for this operation'
        ) {
          throw new UserMessageError(
            'This sample cannot accept reports in its current state. Check the sample status before uploading again.'
          )
        }
        throw new Error(status?.status ?? 'Mangler bekreftelse på mottak')
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['requisitions']
      })
    }
  })
}
